import type { ChatRoomResponse } from '../api/routes';
import type { ChatRoom } from '../types';
import { sortRelatedBulletins } from './sortBulletins';
import { isAccessibleChatRoom } from './isAccessibleChat';

export const mapApiRoomToChatRoom = (
  room: ChatRoomResponse,
  isUserOnline?: (userId: number) => boolean,
): ChatRoom | null => {
  const otherUserId = room.other_user?.id_user;
  if (
    !isAccessibleChatRoom({ room_id: room.room_id, other_user_id: otherUserId }) ||
    otherUserId == null ||
    otherUserId <= 0 ||
    !room.other_user
  ) {
    return null;
  }

  const otherUser = {
    id: room.other_user.id_user,
    id_user: room.other_user.id_user,
    id_profile: room.other_user.id_profile,
    profile_type:
      room.other_user.id_role === 1 ||
      room.other_user.role_text?.toLowerCase().includes('репетитор')
        ? ('teacher' as const)
        : ('student' as const),
    name: room.other_user.name,
    surname: room.other_user.surname,
    patronymic: room.other_user.patronymic,
    description: room.other_user.role_text,
    status: 'available',
    online: (isUserOnline?.(otherUserId) ?? false) || Boolean(room.other_user.is_online),
    last_seen_at: room.other_user.last_seen_at ?? null,
    deleted: room.other_user.is_deleted,
    sex: 0,
    list_skills: [],
    rating: 0,
    geo_range: {},
    mode: 'online',
    experience: 0,
    date_create: new Date().toISOString(),
    avatar:
      room.other_user.profile_photo_token &&
      room.other_user.id_role !== 2 &&
      !room.other_user.role_text?.toLowerCase().includes('ученик')
        ? `/get_image/${room.other_user.profile_photo_token}`
        : undefined,
  };

  const lastMessage = room.last_message
    ? {
        id: room.last_message.id.toString(),
        text: room.last_message.msg,
        sender_id: room.last_message.user_id,
        receiver_id: otherUserId,
        timestamp: new Date(room.last_message.date_send).toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        created_at: room.last_message.date_send,
        status: 'sent' as const,
      }
    : undefined;

  const relatedBulletins = sortRelatedBulletins(
    (room.related_bulletins ?? []).map((b) => ({
      bulletin_id: b.bulletin_id,
      bulletin_response_id: b.bulletin_response_id,
      status: b.status,
      active: b.active,
      bulletin_closed: b.bulletin_closed,
      date_create: b.date_create,
      title: b.title,
      subcategory_ru: b.subcategory_ru,
      category_ru: b.category_ru,
    })),
  );

  return {
    id: room.id.toString(),
    room_id: room.room_id,
    other_user_id: otherUserId,
    other_user: otherUser,
    last_message: lastMessage,
    unread_count: room.unread_count ?? 0,
    updated_at: room.last_message?.date_send || room.date_start,
    date_start: room.date_start,
    active: room.active,
    message_count: room.message_count,
    related_bulletins: relatedBulletins,
    moderation_blocked: Boolean(room.moderation_blocked),
    moderation_blocked_reason: room.moderation_blocked_reason ?? null,
  };
};
